import { Button, Grid, HStack, Icon, Spinner, Stack, StackDivider, Text, VStack } from '@chakra-ui/react';
import React, { useState } from 'react';
import { MdOutlineCancel, MdOutlineDone, MdOutlineOpenInNew } from 'react-icons/md';
import { getErrorMessage, getKycDocStatusColor } from '@utils';
import { ALERT_STATUS_TYPE, KYC_DOC_STATUS, ROLES } from '@constants';
import useIsAdmin from '@hooks/useIsAdmin';
import { AiOutlineFolderOpen } from 'react-icons/ai';
import api from '@api/endpoints';
import { useDispatch } from 'react-redux';
import { setAlert } from '@store/actions/alertActions';
import useAuthRole from '@hooks/useAuthRole';
import { canRejectDataType, canVerifyDataType, canUnblockDataType } from '@views/Kyc/kycUiUtils';

const KycDocCard = ({
  onKycDocActionClick,
  onKycInfoClick,
  onKycDataReqClick,
  doc,
  userId,
  onRemarksClick,
  onRejectReasonClick,
  onORKYCVerifyClick,
  kycProvider,
  kycSubProvider,
  onVerifyClick,
  onVerifyClickV2
}) => {
  const isAdmin = useIsAdmin();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { hasAnyAuthority } = useAuthRole();

  const handleDocumentTypeClick = () => {
    setIsLoading(true);
    api.workflow
      .docByDocType({ userId, documentTye: doc.subDataType })
      .then((res) => res.data)
      .then((data) => window.open(data.url))
      .catch((error) => {
        dispatch(
          setAlert({
            title: getErrorMessage({
              error,
              genericMessage:
                'Something went wrong while fetching the document!'
            }),
            status: ALERT_STATUS_TYPE.ERROR
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleVerifyClick = () => {
    if (onVerifyClickV2) {
      onVerifyClickV2({
        provider: kycProvider,
        subProvider: kycSubProvider,
        payload: {
          kyc: doc,
          newStatus: KYC_DOC_STATUS.VERIFIED
        }
      });
      return null;
    }
  };

  return (
    <VStack
      w={{ base: '100%', md: '100%' }}
      maxW={'800px'}
      bg="backGround"
      borderRadius="12px"
      divider={
        <StackDivider style={{ margin: '0' }} borderColor="whiteAlpha.300" />
      }
    >
      <Grid
        gridTemplateColumns={'repeat(2,auto)'}
        rowGap={2}
        w={'90%'}
        justifyContent="space-between"
        m={'1rem'}
      >
        <HStack>
          <Text fontSize={{ base: 'sm', sm: 'md' }} color="gray">
            {doc.dataType}
          </Text>
        </HStack>

        <HStack>
          <Icon
            viewBox="0 0 200 200"
            color={getKycDocStatusColor(doc.kycStatus)}
            w="8px"
          >
            <path
              fill="currentColor"
              d="M 100, 100 m -75, 0 a 75,75 0 1,0 150,0 a 75,75 0 1,0 -150,0"
            />
          </Icon>
          <Text fontSize={{ base: 'sm', sm: 'md' }} color="white">
            {doc.kycStatus}
          </Text>
        </HStack>
      </Grid>
      <Grid
        gridTemplateColumns={{
          base: '1fr',
          md: isAdmin ? `1fr 0.7fr 0.7fr 115px 115px` : `1fr 1fr 1fr`
        }}
        gap={2}
        alignItems={'flex-start'}
        w={'90%'}
        m={'1rem'}
        textAlign={{ base: 'end', md: 'start' }}
      >
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
          height={'100%'}
        >
          <Text fontSize={'sm'} color={'gray'}>
            Sub Doc Type
          </Text>
          {doc.subDataType && doc.subDataType !== 'NA' ? (
            <HStack cursor={'pointer'} onClick={handleDocumentTypeClick}>
              {isLoading ? (
                <Spinner size={'sm'} color={'white'} />
              ) : (
                <AiOutlineFolderOpen color={'white'} />
              )}
              <Text fontSize={'sm'} color={'white'}>
                {doc.subDataType}
              </Text>
            </HStack>
          ) : (
            <Text fontSize={'sm'} color={'white'}>
              -
            </Text>
          )}
        </Stack>
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
          height={'100%'}
        >
          <Text fontSize={'sm'} color={'gray'}>
            Provider Info
          </Text>
          {doc.providerInfo?.length ? (
            <MdOutlineOpenInNew
              color={'gray'}
              cursor={'pointer'}
              onClick={() =>
                onKycInfoClick({ kycDetailsId: doc.id, kycInfo: doc.providerInfo })
              }
            />
          ) : (
            <Text fontSize={'sm'} color={'white'}>
              -
            </Text>
          )}
        </Stack>
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
          height={'100%'}
        >
          <Text fontSize={'sm'} color={'gray'}>
            Data Req
          </Text>
          {doc.dataReq?.length ? (
            <MdOutlineOpenInNew
              color={'gray'}
              cursor={'pointer'}
              onClick={() =>
                onKycDataReqClick({
                  kycDetailsId: doc?.id,
                  kycInfo: doc?.dataReq
                })
              }
            />
          ) : (
            <Text fontSize={'sm'} color={'white'}>
              -
            </Text>
          )}
        </Stack>
        <Stack
          direction={{ base: 'row', md: 'column' }}
          justifyContent={'space-between'}
          height={'100%'}
        >
          <Text fontSize={'sm'} color={'gray'}>
            Remarks
          </Text>
          {doc.remarks?.length ? (
            <MdOutlineOpenInNew
              color={'gray'}
              cursor={'pointer'}
              onClick={() =>
                onRemarksClick({
                  kycDetailsId: doc.id,
                  kycType: doc.dataType,
                  remarks: doc.remarks
                })
              }
            />
          ) : (
            <Text fontSize={'sm'} color={'white'}>
              -
            </Text>
          )}
        </Stack>

        {doc?.status === KYC_DOC_STATUS.REJECTED && (
          <Stack
            direction={{ base: 'row', md: 'column' }}
            justifyContent={'space-between'}
            height={'100%'}
          >
            <Text fontSize={'sm'} color={'gray'}>
              Rejection Reason
            </Text>
            {doc.rejectionReason?.length ? (
              <MdOutlineOpenInNew
                color={'gray'}
                cursor={'pointer'}
                onClick={() =>
                  onRejectReasonClick({
                    kycDetailsId: doc.id,
                    kycType: doc.dataType,
                    rejectionReason: doc.rejectionReason
                  })
                }
              />
            ) : (
              <Text fontSize={'sm'} color={'white'}>
                -
              </Text>
            )}
          </Stack>
        )}

        {hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE) &&
          doc.dataType === 'DUPLICATE_CHECK' &&
          doc.kycStatus === KYC_DOC_STATUS.REJECTED && (
            <>
              <Button
                mt={{ base: 2, md: 0 }}
                ml={{ base: 0, md: 1 }}
                rightIcon={<MdOutlineCancel />}
                type="submit"
                rounded="full"
                colorScheme="red"
                alignSelf={{ base: 'center', md: 'center' }}
                variant={'outline'}
                px={5}
                onClick={() => {
                  onKycDocActionClick({
                    kyc: doc,
                    newStatus: KYC_DOC_STATUS.PENDING_FOR_VERIFICATION
                  });
                }}
              >
                Unblock
              </Button>
            </>
          )}
        {hasAnyAuthority(ROLES.MANAGER_ROLE, ROLES.ADMIN_ROLE) && (
          <>
            {canUnblockDataType(kycProvider, doc.dataType, doc.kycStatus, { rejectionReasons: doc.rejectionReasons }) && (
              <Button
                mt={{ base: 2, md: 0 }}
                ml={{ base: 0, md: 1 }}
                rightIcon={<MdOutlineCancel />}
                type="submit"
                rounded="full"
                colorScheme="green"
                alignSelf={{ base: 'center', md: 'center' }}
                variant={'outline'}
                px={5}
                onClick={() => {
                  onKycDocActionClick({
                    kyc: doc,
                    newStatus: KYC_DOC_STATUS.PENDING_FOR_VERIFICATION
                  });
                }}
              >
                Unblock
              </Button>
            )}
            {canRejectDataType(kycProvider, doc.dataType, doc.kycStatus) && (
              <Button
                mt={{ base: 2, md: 0 }}
                ml={{ base: 0, md: 1 }}
                rightIcon={<MdOutlineCancel />}
                type="submit"
                rounded="full"
                colorScheme="red"
                alignSelf={{ base: 'center', md: 'center' }}
                variant={'outline'}
                px={5}
                onClick={() => {
                  onKycDocActionClick({
                    kyc: doc,
                    newStatus: KYC_DOC_STATUS.REJECTED
                  });
                }}
              >
                Reject
              </Button>
            )}
            {canVerifyDataType(kycProvider, doc.dataType, doc.kycStatus) && (
              <Button
                mt={{ base: 2, md: 0 }}
                ml={{ base: 0, md: 1 }}
                rightIcon={<MdOutlineDone color="black" />}
                type="submit"
                rounded="full"
                color={'black'}
                colorScheme="brand"
                alignSelf={{ base: 'center', md: 'center' }}
                px={5}
                onClick={handleVerifyClick}
              >
                Verify
              </Button>
            )}
          </>
        )}
      </Grid>
    </VStack>
  );
};

export default KycDocCard;
